/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import { useMaterialUIController } from "context";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import { Card, Pagination, Chip, Tooltip, Menu, MenuItem, Grid } from "@mui/material";
import MDTypography from "components/MDTypography";
import Footer from "examples/Footer";
import MDBadge from "components/MDBadge";
import SkLoading from "components/SkLoading";
import SkModal from "components/SkModal";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import AstrieskIcon from "components/AstrieskIcon";
import DataTable from "examples/Tables/DataTable";
import { SkPrice, SkDate } from "Utils/dateFunc";
import { useDispatch } from "react-redux";
import { handleAlert } from "redux/festures/alertSlice";
import http from "Utils/api2";

const columns = [
  { Header: "S.No", accessor: "no", align: "left" },
  { Header: "Partner Details", accessor: "partnerDetails", align: "left" },
  { Header: "Bank Info", accessor: "bankInfo", align: "left" },
  { Header: "Amount", accessor: "amount", align: "center" },
  { Header: "Status", accessor: "status", align: "center" },
  { Header: "Requested At", accessor: "date", align: "center" },
];

const TransactionSection = () => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const dispatch = useDispatch();

  const [rowsData, setRowsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [partnerFilter, setPartnerFilter] = useState("");

  // Status dropdown state
  const [statusAnchor, setStatusAnchor] = useState(null);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);

  // Complete modal state
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [transactionRef, setTransactionRef] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Reject modal state
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const fetchWithdrawals = () => {
    setLoading(true);
    http
      .get(`/admin-withdrawals?page=${page}&partnerId=${partnerFilter}`)
      .then((res) => {
        if (res?.data?.success) {
          const list = res?.data?.data || [];
          setTotalPage(res?.data?.pagination?.pages || 1);
          
          if (list.length > 0) {
            const formattedRows = list.map((value, index) => ({
              no: (
                <MDTypography sx={{ fontSize: 13, fontWeight: "medium" }} variant="text">
                  {(page - 1) * 20 + index + 1}
                </MDTypography>
              ),
              partnerDetails: (
                <MDBox display="flex" flexDirection="column">
                  <MDTypography sx={{ fontSize: 13, fontWeight: "medium" }} variant="text">
                    Name: {value?.partnerId?.name || "N/A"}
                  </MDTypography>
                  <MDTypography sx={{ fontSize: 12 }} variant="text" color="text">
                    Email: {value?.partnerId?.email || "N/A"}
                  </MDTypography>
                  <MDTypography sx={{ fontSize: 12 }} variant="text" color="text">
                    Phone: {value?.partnerId?.phoneNumber || "N/A"}
                  </MDTypography>
                </MDBox>
              ),
              bankInfo: (
                <MDBox display="flex" flexDirection="column" style={{ maxWidth: "250px", wordWrap: "break-word" }}>
                  {value?.bankDetails?.upiId ? (
                    <MDTypography sx={{ fontSize: 12 }} variant="text" color="text">
                      UPI: {value.bankDetails.upiId}
                    </MDTypography>
                  ) : null}
                  {value?.bankDetails?.accountHolderName ? (
                    <MDTypography sx={{ fontSize: 12 }} variant="text" color="text">
                      Holder: {value.bankDetails.accountHolderName}
                    </MDTypography>
                  ) : null}
                  {value?.bankDetails?.bankName ? (
                    <MDTypography sx={{ fontSize: 12 }} variant="text" color="text">
                      Bank: {value.bankDetails.bankName}
                    </MDTypography>
                  ) : null}
                  {value?.bankDetails?.ifscCode ? (
                    <MDTypography sx={{ fontSize: 12 }} variant="text" color="text">
                      IFSC: {value.bankDetails.ifscCode}
                    </MDTypography>
                  ) : null}
                </MDBox>
              ),
              amount: (
                <MDBadge
                  color="success"
                  variant="gradient"
                  size="lg"
                  badgeContent={SkPrice(value?.amount) || "N/A"}
                  fontSize={13.5}
                />
              ),
              status: (
                <MDBox
                  onClick={(e) => {
                    setStatusAnchor(e.currentTarget);
                    setSelectedWithdrawal(value);
                  }}
                  sx={{ cursor: "pointer", display: "inline-block" }}
                >
                  <Tooltip title="Update Status">
                    <Chip
                      label={value?.status || "PENDING"}
                      color={
                        value?.status === "COMPLETED"
                          ? "success"
                          : value?.status === "REJECTED"
                            ? "error"
                            : value?.status === "ACCEPTED"
                              ? "info"
                              : "warning"
                      }
                      size="small"
                      sx={{ cursor: "pointer" }}
                    />
                  </Tooltip>
                </MDBox>
              ),
              date: (
                <MDTypography display="block" variant="button" fontWeight="medium">
                  {SkDate(new Date(value?.requestedAt)) || "N/A"}
                </MDTypography>
              ),
            }));
            setRowsData(formattedRows);
          } else {
            setRowsData([]);
          }
        }
      })
      .catch((err) => {
        dispatch(
          handleAlert({
            isOpen: true,
            type: "error",
            msg: err?.response?.data?.message || "Failed to fetch transactions",
          })
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchWithdrawals();
  }, [page, partnerFilter]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // ─── Status Actions ───────────────────────────────────────────

  const handleAccept = async () => {
    setStatusAnchor(null);
    if (!selectedWithdrawal) return;
    setActionLoading(true);
    try {
      const res = await http.post(`/withdrawal/${selectedWithdrawal._id}/accept`);
      dispatch(
        handleAlert({
          isOpen: true,
          type: res?.data?.success ? "success" : "error",
          msg: res?.data?.message || "Withdrawal accepted",
        })
      );
      if (res?.data?.success) fetchWithdrawals();
    } catch (err) {
      dispatch(
        handleAlert({
          isOpen: true,
          type: "error",
          msg: err?.response?.data?.message || "Failed to accept withdrawal",
        })
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenCompleteModal = () => {
    setStatusAnchor(null);
    setTransactionRef("");
    setCompleteModalOpen(true);
  };

  const handleCompleteSubmit = async () => {
    if (!transactionRef.trim()) {
      dispatch(
        handleAlert({ isOpen: true, type: "error", msg: "Transaction reference ID is required" })
      );
      return;
    }
    setActionLoading(true);
    try {
      const res = await http.post(`/withdrawal/${selectedWithdrawal._id}/complete`, {
        transactionRef: transactionRef.trim(),
      });
      dispatch(
        handleAlert({
          isOpen: true,
          type: res?.data?.success ? "success" : "error",
          msg: res?.data?.message || "Withdrawal marked as completed",
        })
      );
      if (res?.data?.success) {
        setCompleteModalOpen(false);
        fetchWithdrawals();
      }
    } catch (err) {
      dispatch(
        handleAlert({
          isOpen: true,
          type: "error",
          msg: err?.response?.data?.message || "Failed to complete withdrawal",
        })
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenRejectModal = () => {
    setStatusAnchor(null);
    setRejectReason("");
    setRejectModalOpen(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      dispatch(
        handleAlert({ isOpen: true, type: "error", msg: "Rejection reason is required" })
      );
      return;
    }
    setActionLoading(true);
    try {
      const res = await http.post(`/withdrawal/${selectedWithdrawal._id}/reject`, {
        rejectionReason: rejectReason.trim(),
      });
      dispatch(
        handleAlert({
          isOpen: true,
          type: res?.data?.success ? "success" : "error",
          msg: res?.data?.message || "Withdrawal rejected",
        })
      );
      if (res?.data?.success) {
        setRejectModalOpen(false);
        fetchWithdrawals();
      }
    } catch (err) {
      dispatch(
        handleAlert({
          isOpen: true,
          type: "error",
          msg: err?.response?.data?.message || "Failed to reject withdrawal",
        })
      );
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h6" color="white">
                Withdrawal Requests
              </MDTypography>
            </MDBox>
            <MDBox py={3}>
              {loading ? (
                <SkLoading />
              ) : rowsData && rowsData.length > 0 ? (
                <>
                  <DataTable
                    table={{ columns, rows: rowsData }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                    pagination={false}
                    isPagination={false}
                  />
                  {totalPage > 1 && (
                    <MDBox display="flex" justifyContent="center" py={3}>
                      <Pagination
                        count={totalPage}
                        page={page}
                        onChange={handlePageChange}
                        color="info"
                        shape="rounded"
                      />
                    </MDBox>
                  )}
                </>
              ) : (
                <MDBox display="flex" justifyContent="center" py={5}>
                  <MDTypography variant="h6">No Withdrawals Found</MDTypography>
                </MDBox>
              )}
            </MDBox>
          </Card>
        </MDBox>
        <Footer />
      </DashboardLayout>

      {/* ─── Status Dropdown Menu ─────────────────────────────────── */}
      <Menu
        anchorEl={statusAnchor}
        open={Boolean(statusAnchor)}
        onClose={() => setStatusAnchor(null)}
      >
        {selectedWithdrawal?.status === "PENDING" && [
          <MenuItem key="accept" onClick={handleAccept}>Accept</MenuItem>,
          <MenuItem key="reject" onClick={handleOpenRejectModal}>Reject</MenuItem>,
        ]}
        {selectedWithdrawal?.status === "ACCEPTED" && [
          <MenuItem key="complete" onClick={handleOpenCompleteModal}>Complete</MenuItem>,
          <MenuItem key="reject" onClick={handleOpenRejectModal}>Reject</MenuItem>,
        ]}
      </Menu>

      {/* ─── Complete Modal (Transaction Ref) ─────────────────────── */}
      <SkModal
        show={completeModalOpen}
        unShow={setCompleteModalOpen}
        width={{ sx: "100%", md: "50%" }}
        height={"auto"}
        padding={3}
        overflowY={true}
      >
        <Grid item xs={12}>
          <MDTypography variant="h6">
            Transaction Reference ID <AstrieskIcon />
          </MDTypography>
          <MDInput
            fullWidth
            name="transactionRef"
            value={transactionRef}
            onChange={(e) => setTransactionRef(e.target.value)}
            placeholder="Enter transaction reference ID"
            sx={{ mt: 1 }}
          />
        </Grid>
        <MDBox
          mt={3}
          sx={{
            display: "flex",
            justifyContent: { xs: "stretch", md: "flex-end" },
            gap: 1.5,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setCompleteModalOpen(false)}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleCompleteSubmit}
            color="info"
            variant="gradient"
            disabled={actionLoading}
          >
            {actionLoading ? "Submitting..." : "Submit"}
          </MDButton>
        </MDBox>
      </SkModal>

      {/* ─── Reject Modal (Reason) ────────────────────────────────── */}
      <SkModal
        show={rejectModalOpen}
        unShow={setRejectModalOpen}
        width={{ sx: "100%", md: "50%" }}
        height={"auto"}
        padding={3}
        overflowY={true}
      >
        <Grid item xs={12}>
          <MDTypography variant="h6">
            Reason for Rejection <AstrieskIcon />
          </MDTypography>
          <MDInput
            fullWidth
            name="rejectReason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter reason for rejection"
            multiline
            rows={4}
            sx={{ mt: 1 }}
          />
        </Grid>
        <MDBox
          mt={3}
          sx={{
            display: "flex",
            justifyContent: { xs: "stretch", md: "flex-end" },
            gap: 1.5,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setRejectModalOpen(false)}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleRejectSubmit}
            color="info"
            variant="gradient"
            disabled={actionLoading}
          >
            {actionLoading ? "Submitting..." : "Submit"}
          </MDButton>
        </MDBox>
      </SkModal>
    </>
  );
};

export default TransactionSection;
